/* Common Imports */

import React from "react";
import { styled } from "@mui/system";

/* Redux & Type Imports */

import { NextRouter } from "next/router";
import StateInterface from "../../../redux-magic/state-interface";
import { setBroadcastFormThunk } from "../../../redux-magic/store";

/* Component Imports */

import { CircularProgress, Typography } from "@mui/material";

/* Icon Imports */

import { EmojiObjectsOutlined } from "@mui/icons-material";
import { ObjectId } from "bson";

/* Styled Components */

const Container = styled("div")({
	display: "flex",
	flexDirection: "column",
	justifyContent: "start",
	alignItems: "center",
	width: "100%",
	height: "100%",
	minHeight: "100%",
	flex: 1,
	position: "relative",
});

const HeaderSection = styled("div")({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "center",
	backgroundImage: "url('/images/broadcast/AgentScreenBackground.svg')",
	width: "100%",
	backgroundSize: "cover",
	backgroundRepeat: "no-repeat",
	height: "20rem",
	padding: "2rem 0rem 0rem 0rem",
	gap: "0.5rem",
});

const Title = styled(Typography)({
	fontWeight: 700,
	fontSize: "1.25rem",
	textAlign: "center",
	color: "#FFFFFF",
});

const SubTitle = styled(Typography)({
	color: "#FFFFFF",
	fontWeight: 400,
	fontSize: "0.875rem",
	textAlign: "center",
});

const BroadcastBox = styled("div")({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	width: "100%",
	borderRadius: "1rem",
	margin: "3rem auto 3rem auto",
	gap: "0.75rem",
});

const BroadcastLabel = styled(Typography)({
	fontWeight: 700,
	fontSize: "0.875rem",
	textAlign: "center",
});

const BroadcastNumber = styled(Typography)({
	color: "#FCB24F",
	fontWeight: 700,
	WebkitTextStroke: "2px #000",
	textShadow: `
        -1px -1px 0 #000,  
        1px -1px 0 #000,
        -1px  1px 0 #000,
        1px  1px 0 #000
    `,
	textAlign: "center",
	fontSize: "6rem",
	lineHeight: "6rem",
});

const TipContainer = styled("div")({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "flex-start",
	borderRadius: "1rem",
	background: "#FFF3E0",
	gap: "0.5rem",
	padding: "1rem",
	margin: "0rem 1rem 2rem 1rem",
});

const Tip = styled(Typography)({
	fontWeight: 400,
	fontSize: "0.75rem",
});

export default function NoOfAgentScreen({
	dispatch,
	router,
	broadcast,
	userId,
	business_profile_id,
}: {
	dispatch: Function;
	router: NextRouter;
	broadcast: StateInterface["broadcast_data"] | undefined | null;
	userId: string | ObjectId;
	business_profile_id: string | ObjectId | undefined | null;
}) {
	/* Translations */

	const localLang = {
		days: router.locale === "ar-AE" ? "يوم " : router.locale === "kn-IN" ? " ದಿನಗಳು" : " days",
		beegru_broadcast:
			router.locale === "ar-AE" ? "بث بيجرو" : router.locale === "kn-IN" ? "ಬೀಗ್ರು ಪ್ರಸಾರ" : "Beegru Broadcast",
		free_for_a_limited_time:
			router.locale === "ar-AE"
				? "مجاني لفترة محدودة"
				: router.locale === "kn-IN"
					? "ಮಿತಿಯಲ್ಲಿನ ಉಚಿತ"
					: "Free for a limited time",
		your_broadcast_will_reach:
			router.locale === "ar-AE"
				? "سيصل بثك إلى"
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಪ್ರಸಾರವು ತಲುಪುತ್ತದೆ"
					: "Your broadcast will reach",
		agents_on_beegru:
			router.locale === "ar-AE"
				? "وكلاء على بيجرو"
				: router.locale === "kn-IN"
					? "ಬೀಗ್ರುದಲ್ಲಿ ಏಜೆಂಟ್‌ಗಳು"
					: "agents on Beegru",
		tip_content:
			router.locale === "ar-AE"
				? "تمكنك ميزة البث من تنشيط شبكة Beegru من الوكلاء، مما يتيح لك الوصول إلى جمهور أوسع وإغلاق الصفقات بشكل أسرع."
				: router.locale === "kn-IN"
					? "ಬೀಗ್ರು ಪ್ರಸಾರ ವೈಶಿಷ್ಟ್ಯವು ನಿಮ್ಮನ್ನು ಬೀಗ್ರು ಏಜೆಂಟ್‌ಗಳ ಜಾಲವನ್ನು ಸಕ್ರಿಯಗೊಳಿಸಲು ಅನುಮತಿಸುತ್ತದೆ, ಇದರಿಂದ ನೀವು ವ್ಯಾಪಕ ಪ್ರೇಕ್ಷಕರಿಗೆ ತಲುಪಬಹುದು ಮತ್ತು ಒಪ್ಪಂದಗಳನ್ನು ವೇಗವಾಗಿ ಮುಗಿಸಬಹುದು"
					: "The broadcast feature enables you to activate Beegru's network of agents, helping you to reach a wider audience and close deals faster",
	};

	const [loadingAgents, setLoadingAgents] = React.useState<boolean>(false);

	React.useEffect(() => {
		const url = `${process.env.PRODUCTION_API_URL}broadcast/fetch-no-of-agents`;

		setLoadingAgents(true);

		fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "text/plain",
			},
			body: JSON.stringify({
				creator_user_id: userId,
				creator_business_profile_id: business_profile_id,
			}),
		})
			.then((res) => res.json())
			.then((data) => {
				if (data.status === "success") {
					dispatch(
						setBroadcastFormThunk({
							commission_offered: broadcast?.commission_offered || 0,
							no_of_agents: data.payload || 0,
							notes: broadcast?.notes || "",
						}),
					);
					setLoadingAgents(false);
				} else {
					console.error("Error fetching business data:", data.message);
					setLoadingAgents(false);
				}
			})
			.catch((err) => {
				console.error("Fetch error:", err);
				setLoadingAgents(false);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Container>
			{/* Header */}

			<HeaderSection>
				<Title variant="h6">{localLang.beegru_broadcast}</Title>
				<SubTitle variant="body2">{localLang.free_for_a_limited_time}</SubTitle>
			</HeaderSection>

			{/* No. of Agents */}

			<BroadcastBox>
				<BroadcastLabel variant="body2">{localLang.your_broadcast_will_reach}</BroadcastLabel>
				{loadingAgents || broadcast?.no_of_agents === 0 ? (
					<CircularProgress
						sx={{
							color: "#FCB24F",
						}}
						size={96}
					/>
				) : (
					<BroadcastNumber variant="h1">{broadcast?.no_of_agents || 0}</BroadcastNumber>
				)}

				<BroadcastLabel variant="body2">{localLang.agents_on_beegru}</BroadcastLabel>
			</BroadcastBox>

			{/* Tip */}

			<TipContainer>
				<EmojiObjectsOutlined
					sx={{
						color: "#FFB800",
						fontSize: "1.5rem",
					}}
				/>
				<Tip variant="body1">{localLang.tip_content}</Tip>
			</TipContainer>
		</Container>
	);
}
